import { useState } from 'react'
import { router } from '@inertiajs/react'
import { ChevronLeft, TextSelect, Plus, Pencil, Trash2, X, Check } from 'lucide-react'

interface GemCriteriaTemplate {
  id: string
  title: string
  description: string
}

interface GemCriteriaTemplatesProps {
  templates: GemCriteriaTemplate[]
}

export default function GemCriteriaTemplatesIndex({ templates: initialTemplates }: GemCriteriaTemplatesProps) {
  const [templates, setTemplates] = useState<GemCriteriaTemplate[]>(initialTemplates)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [newTitle, setNewTitle] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

  const resetAddForm = () => {
    setNewTitle('')
    setNewDescription('')
    setShowAddForm(false)
  }

  const resetEditForm = () => {
    setEditingId(null)
    setEditTitle('')
    setEditDescription('')
  }

  const handleCreate = async () => {
    if (!newTitle.trim() || !newDescription.trim()) return

    setIsSubmitting(true)
    try {
      const response = await fetch('/gem_criteria_templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          gem_criteria_template: {
            title: newTitle.trim(),
            description: newDescription.trim(),
          },
        }),
      })

      if (response.ok) {
        const data = await response.json()
        setTemplates([...templates, data.template])
        resetAddForm()
      }
    } catch (error) {
      console.error('Failed to create template:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdate = async (id: string) => {
    if (!editTitle.trim() || !editDescription.trim()) return

    setIsSubmitting(true)
    try {
      const response = await fetch(`/gem_criteria_templates/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          gem_criteria_template: {
            title: editTitle.trim(),
            description: editDescription.trim(),
          },
        }),
      })

      if (response.ok) {
        const data = await response.json()
        setTemplates(templates.map((t) => (t.id === id ? data.template : t)))
        resetEditForm()
      }
    } catch (error) {
      console.error('Failed to update template:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    setIsSubmitting(true)
    try {
      const response = await fetch(`/gem_criteria_templates/${id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      })

      if (response.ok) {
        setTemplates(templates.filter((t) => t.id !== id))
        setDeleteConfirmId(null)
      }
    } catch (error) {
      console.error('Failed to delete template:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const startEditing = (template: GemCriteriaTemplate) => {
    setEditingId(template.id)
    setEditTitle(template.title)
    setEditDescription(template.description)
    setShowAddForm(false)
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
        <div className="max-w-2xl mx-auto px-6 py-6">
          {/* Breadcrumb */}
          <button
            onClick={() => router.visit('/reports')}
            className="inline-flex items-center gap-1 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors mb-6"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Reports</span>
          </button>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center">
              <TextSelect className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-stone-900 dark:text-stone-100">
                Gem Criteria Templates
              </h1>
              <p className="text-sm text-stone-500 dark:text-stone-400">
                Reusable templates for gem criteria
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        {/* Add New Template Section */}
        <section className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Plus className="w-4 h-4 text-stone-400" />
              <h2 className="text-sm font-medium text-stone-900 dark:text-stone-100">
                Add New Template
              </h2>
            </div>
            {!showAddForm && (
              <button
                onClick={() => {
                  setShowAddForm(true)
                  resetEditForm()
                }}
                className="px-3 py-1.5 text-sm font-medium rounded-lg bg-teal-600 hover:bg-teal-700 text-white transition-colors"
              >
                New Template
              </button>
            )}
          </div>

          {showAddForm && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5">
                  Template Name
                </label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-stone-200 dark:border-stone-700 rounded-lg bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  placeholder="e.g., Competitor Mention"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5">
                  Description
                </label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-stone-200 dark:border-stone-700 rounded-lg bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none"
                  placeholder="Describe what the AI should look for..."
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCreate}
                  disabled={!newTitle.trim() || !newDescription.trim() || isSubmitting}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Saving...' : 'Save Template'}
                </button>
                <button
                  onClick={resetAddForm}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!showAddForm && (
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Create templates to quickly add commonly used gem criteria to your streams.
            </p>
          )}
        </section>

        {/* Templates List */}
        <section className="space-y-4">
          <h2 className="text-sm font-medium text-stone-700 dark:text-stone-300 px-1">
            Your Templates ({templates.length})
          </h2>

          {templates.length === 0 ? (
            <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-8 text-center">
              <div className="w-12 h-12 rounded-xl bg-stone-100 dark:bg-stone-800 flex items-center justify-center mx-auto mb-4">
                <TextSelect className="w-6 h-6 text-stone-400" />
              </div>
              <h3 className="text-sm font-medium text-stone-900 dark:text-stone-100 mb-1">
                No templates yet
              </h3>
              <p className="text-sm text-stone-500 dark:text-stone-400 mb-4">
                Create your first template to save time when adding gem criteria.
              </p>
              <button
                onClick={() => setShowAddForm(true)}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-teal-600 hover:bg-teal-700 text-white transition-colors"
              >
                Create Template
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4"
                >
                  {editingId === template.id ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5">
                          Template Name
                        </label>
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-stone-200 dark:border-stone-700 rounded-lg bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5">
                          Description
                        </label>
                        <textarea
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 text-sm border border-stone-200 dark:border-stone-700 rounded-lg bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdate(template.id)}
                          disabled={!editTitle.trim() || !editDescription.trim() || isSubmitting}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Check className="w-3.5 h-3.5" />
                          Save
                        </button>
                        <button
                          onClick={resetEditForm}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-stone-900 dark:text-stone-100 mb-1">
                          {template.title}
                        </h3>
                        <p className="text-sm text-stone-500 dark:text-stone-400 line-clamp-2">
                          {template.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {deleteConfirmId === template.id ? (
                          <>
                            <button
                              onClick={() => handleDelete(template.id)}
                              disabled={isSubmitting}
                              className="p-1.5 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                              title="Confirm delete"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirmId(null)}
                              className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEditing(template)}
                              className="p-1.5 rounded-lg text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                              title="Edit template"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirmId(template.id)}
                              className="p-1.5 rounded-lg text-stone-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                              title="Delete template"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  )
}
